/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { updateFn } from '../../form/common';
import { DatePicker } from '../../date-picker';
import { getDate, getDateTime, getDiff, getFutureDate, isBefore, subtractDate } from '../../../utils/date';
import { BasicAlert } from '../../alert/BasicAlert';
import './SurveyDeadlineReminder.scss';
import { ScheduledDate } from '../../../model/surveyData';

export const ERROR_MESSAGE = {
  LIMITED: 'Reminder earliest day should be tomorrow',
};

interface SurveyDeadlineReminderProps {
  deadlineDate?: string;
  scheduledDates?: ScheduledDate[];
  updateForm: updateFn;
}

export default function SurveyDeadlineReminder(props: SurveyDeadlineReminderProps) {
  const { deadlineDate, scheduledDates, updateForm } = props;
  const [hasDeadline, setHasDeadline] = useState(!!deadlineDate);
  const [errorMessage, setErrorMessage] = useState('');

  const [scheduledDate] = scheduledDates ?? [];
  const minReminderDate = getDateTime(getFutureDate(1), { hour: 0, minute: 0, second: 0 });

  const handleCheck = (event: React.FormEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    if (!checked) {
      updateForm({ code: 'deadlineDate', value: undefined });
    }
    setHasDeadline(checked);
    setErrorMessage('');
  };

  const handleChangeDeadline = (date: Date | undefined) => {
    updateForm({ code: 'deadlineDate', value: date ? getDateTime(date).toISOString() : date });
  };

  const handleChangeSchedule = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const updateScheduledDate = value
      ? subtractDate(deadlineDate ?? new Date(), Number(value), 'day', false)
      : getDate(deadlineDate).toDate();

    if (isBefore(minReminderDate, updateScheduledDate)) {
      setErrorMessage(ERROR_MESSAGE.LIMITED);
    } else {
      setErrorMessage('');
    }

    // When update scheduled date, we keep generated idempotencyKey and update only the date
    updateForm({
      code: 'scheduledDates',
      value: [
        {
          idempotencyKey: scheduledDate?.idempotencyKey,
          date: updateScheduledDate,
        },
      ],
    });
  };

  const reminderDays = deadlineDate && scheduledDate ? getDiff(deadlineDate, scheduledDate.date, 'day') : '';

  return (
    <div className='mt-5 deadline-container'>
      <h5 className='text-ThemeAccentExtradark'>Deadline reminder</h5>
      <FormGroup>
        <Input id='survey-deadline' type='checkbox' className='mr-2' checked={hasDeadline} onChange={handleCheck} />
        <Label check htmlFor='survey-deadline'>
          Send reminder email/notification to all delegated users ahead of the survey deadline (includes both
          contributors and verifiers)
        </Label>
      </FormGroup>
      <DatePicker
        date={deadlineDate ? new Date(deadlineDate) : undefined}
        callbackChange={handleChangeDeadline}
        disabled={!hasDeadline}
        minDate={minReminderDate}
      />
      <div className='d-inline-flex align-items-center mt-3'>
        <span className='me-2'>Send notification reminder</span>
        <Input
          type='number'
          className='deadline-reminder-date py-0 px-1 text-md'
          min={0}
          value={reminderDays}
          onChange={handleChangeSchedule}
          disabled={!deadlineDate}
        />
        <span className='ms-2'>days before deadline</span>
      </div>
      {errorMessage && <BasicAlert type='danger' className='mt-2'>{errorMessage}</BasicAlert>}
    </div>
  );
}
